import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GameStateAuditEntry } from '@/lib/api';

interface UseAdminAuditLogOptions {
  userId?: string;
  keyword?: string;
  limit?: number;
  offset?: number;
}

interface UseAdminAuditLogReturn {
  entries: GameStateAuditEntry[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useAdminAuditLog(options?: UseAdminAuditLogOptions): UseAdminAuditLogReturn {
  const [entries, setEntries] = useState<GameStateAuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const userId = options?.userId;
  const keyword = options?.keyword;
  const limit = options?.limit;
  const offset = options?.offset;

  const fetchLog = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.adminListGameStateAuditLog({ userId, keyword, limit, offset });
      if (fetchIdRef.current !== id) return;
      setEntries(result.entries);
      setTotal(result.total);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load audit log');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [userId, keyword, limit, offset]);

  const refresh = useCallback(() => {
    setError(null);
    fetchLog();
  }, [fetchLog]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { entries, total, loading, error, refresh };
}
